import type { ReactNode } from 'react';
import { PlayIcon } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';

export function extractYouTubeVideoId(url: string): string | null {
	try {
		const parsed = new URL(url);

		if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1) || null;
		if (parsed.hostname.includes('youtube.com')) {
			if (parsed.pathname.startsWith('/embed/')) {
				return parsed.pathname.replace('/embed/', '') || null;
			}

			if (parsed.pathname.startsWith('/shorts/')) {
				return parsed.pathname.replace('/shorts/', '') || null;
			}

			return parsed.searchParams.get('v');
		}
	} catch {
		// invalid URL
	}

	return null;
}

/**
 * The box a campaign video occupies, whatever is currently inside it — YouTube or our own player.
 *
 * Every state of a gated video — the lazy chunk still loading, consent not yet known, consent refused,
 * the player itself — renders through this, so they all share one definition of the frame. That is the
 * whole point: the video area used to collapse and reflow as it moved between those states, because each
 * one sized itself. Anything that needs a different aspect passes `className` and Tailwind's merge wins,
 * but it then applies to every state at once.
 *
 * The poster is decorative in every state — the campaign's hero image, shown again — so it carries an
 * empty alt unless a title is given, and never announces itself twice to a screen reader.
 */
export function YouTubeFrame(props: { posterSrc?: string; title?: string; className?: string; children?: ReactNode }) {
	return (
		<div className="flex h-full items-center justify-center">
			<div className={cn('bg-muted relative aspect-video w-full overflow-hidden rounded-2xl', props.className)}>
				{props.posterSrc && (
					<img src={props.posterSrc} alt={props.title ?? ''} className="absolute inset-0 size-full object-cover" />
				)}
				{props.children}
			</div>
		</div>
	);
}

/**
 * The video area before anything can be decided: the lazy chunk is still arriving, or the CMP has not
 * been asked yet.
 *
 * Deliberately just the poster — no play button, no notice. Consent is resolved asynchronously, so the
 * server and the first client render cannot know it; showing the refusal affordance here would make every
 * donor who has already accepted watch a "click to load" prompt appear and then vanish. The poster is what
 * the hero would have shown anyway, so for them the only visible change is the image becoming the player.
 */
export function YouTubePending(props: { posterSrc?: string; title?: string; className?: string }) {
	return <YouTubeFrame posterSrc={props.posterSrc} title={props.title} className={props.className} />;
}

/**
 * Stand-in for a YouTube embed the donor has not consented to.
 *
 * Rendering {@link YouTubeEmbed} contacts Google the moment it mounts, which is not something a donor
 * can be assumed to have agreed to — so the player is only mounted once they ask for it (or once the
 * CMP says YouTube is consented). This is the "two-click" pattern, and the click is what makes loading
 * the video a deliberate act rather than a side effect of opening a campaign page.
 *
 * The poster must be a first-party image (the campaign's own hero), never YouTube's thumbnail service:
 * pulling `i.ytimg.com` here would re-introduce on-load contact with Google and defeat the point.
 *
 * Copy comes in through `texts` rather than being translated here, matching `VideoPlayer` — this package
 * carries no catalogs.
 */
export function YouTubePlaceholder(props: {
	posterSrc?: string;
	title?: string;
	className?: string;
	texts: { activate: string; notice: string };
	onActivate: () => void;
}) {
	return (
		<YouTubeFrame posterSrc={props.posterSrc} title={props.title} className={props.className}>
			{/*
			 * No `aria-label`. One would override the whole subtree for assistive technology, so the notice
			 * below — the disclosure that makes this a consent gate rather than a play button — would never
			 * be announced, and a screen-reader user would load a third party having heard only "Play video".
			 * Letting the accessible name come from the contents is verbose but says the same thing a sighted
			 * donor reads before clicking, which is the whole point of the control.
			 */}
			<button
				type="button"
				onClick={props.onActivate}
				className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3 bg-black/50 px-6 py-8 text-center text-white transition-colors hover:bg-black/40"
			>
				<span className="grid size-16 place-items-center rounded-full bg-white/95 transition-transform group-hover:scale-105">
					<PlayIcon className="ml-0.5 size-7 fill-black text-black" />
				</span>
				<span className="text-base font-medium">{props.texts.activate}</span>
				<span className="max-w-md text-xs leading-snug text-white/85">{props.texts.notice}</span>
			</button>
		</YouTubeFrame>
	);
}

/**
 * Takes NO poster, deliberately.
 *
 * The campaign's hero image is the fallback for a campaign that has no working video — it is not a
 * loading state for one that does. Putting it behind the player meant a donor who had already consented
 * saw the hero image for the moment YouTube took to paint, which reads as the page showing the image and
 * then changing its mind. Once the answer is "play it", the video is the only thing that should appear.
 *
 * The frame goes black instead, so that moment reads as a player initialising rather than as a picture.
 *
 * `autoPlay` is for the one caller that has already promised playback: a donor who pressed
 * {@link YouTubePlaceholder}'s "Play video" clicked a control that says it plays the video, and mounting a
 * paused player under `controls=0` would answer that with a frame that looks inert. It is deliberately NOT
 * the default — a video that starts itself on a campaign page nobody asked is the opposite problem — so
 * consent arriving from the CMP still mounts a paused player.
 */
export function YouTubeEmbed(props: { url: string; title?: string; className?: string; autoPlay?: boolean }) {
	const videoId = extractYouTubeVideoId(props.url);
	if (!videoId) return null;

	return (
		<YouTubeFrame className={cn('bg-black', props.className)}>
			{/* oxlint-disable-next-line react/iframe-missing-sandbox */}
			<iframe
				src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&controls=0${props.autoPlay ? '&autoplay=1' : ''}`}
				title={props.title ?? 'Campaign video'}
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				referrerPolicy="strict-origin-when-cross-origin"
				allowFullScreen
				className="absolute inset-0 size-full"
			/>
		</YouTubeFrame>
	);
}
